import { t } from 'i18next';
import { FC, useState } from 'react';
import { useAccountCreationContext } from '../../../common/context/AccountCreationContext';
import { getTypeCategoryFromDisplay } from '../../../common/data/getTypeCategoryFromDisplay';
import { getTypeSubcategoryFromCategory } from '../../../common/data/getTypeSubcategoryFromCategory';
import { getTypeLabelLongFromSubcategory } from '../../../common/data/getTypeLabelLongFromSubcategory';
import { Brand } from '../../../common/types';
import PrimaryTitle from '../../texts/PrimaryTitle';
import PrimaryParagraph from '../../texts/PrimaryParagraph';
import OptionButtonLarge from '../../buttons/OptionButtonLarge';
import { tShirtIconColor } from '../../../common/assets/icon';

const TypeSelectionView: FC<{
	modalIsOpen: boolean;
	modalWidth?: number;
	modalHeight?: number;
	handleProductSelect: (product: {
		type_subcategory: string;
		type_label_long: string;
		brand_code: string;
	}) => void;
	brand: Brand;
	handleBackStep?: () => void;
	handlePreviousBrand?: () => void;
	brandIndex: number;
	typeLabelDisplay: string;
	selectedProduct: any | null;
}> = ({
	modalIsOpen,
	modalWidth,
	modalHeight,
	handleProductSelect,
	brand,
	handleBackStep,
	handlePreviousBrand,
	brandIndex,
	typeLabelDisplay,
	selectedProduct,
}) => {
	const { raw: rawTypeCategory } = getTypeCategoryFromDisplay(typeLabelDisplay || '');
	const subcategoryOptions = rawTypeCategory ? getTypeSubcategoryFromCategory(rawTypeCategory) : [];
	const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

	return (
		<div className="pop-h-full pop-fadeIn pop-overflow-y-auto pop-w-full">
			<div className="pop-flex pop-flex-col pop-w-full">
				<PrimaryTitle className="pop-text-center">{t('product_question')} {brand.name}?</PrimaryTitle>
				<PrimaryParagraph className="pop-text-center pop-my-2">
					{t('type_selection_message')}
				</PrimaryParagraph>

				{/* Brand logo */}
				<div className="pop-relative pop-w-full pop-mb-5">
					<div className="pop-absolute pop-left-0 pop-top-1/2 -pop-translate-y-1/2">
						{brandIndex > 0 && (
							<button
								onClick={handlePreviousBrand}
								className="pop-flex pop-items-center pop-gap-2 pop-text-sm pop-text-popsizePrimary50"
							>
								<span className="pop-text-lg pop-font-bold">&lt;</span>
								{t('previous')}
							</button>
						)}
					</div>

					<div className="pop-flex pop-justify-center pop-items-center pop-h-full">
						{brand?.faviconUrl ? (
							<img className="pop-w-6 pop-h-6" src={brand.faviconUrl} alt="favicon" />
						) : brand?.image ? (
							<img
								className="pop-w-6 pop-h-auto"
								src={`${process.env.REACT_APP_GCP_BUCKET_URL}/images/brand-logos/${brand.image}`}
								alt={brand.name}
							/>
						) : (
							<div className="pop-text-xs pop-text-popsizePrimary50 pop-text-center">
								{brand.label || ''}
							</div>
						)}
					</div>
				</div>

				{/* Type Subcategory Options */}
				<div className="pop-flex pop-flex-wrap pop-justify-center pop-gap-5 pop-w-full">
					{subcategoryOptions.map((subcat, index) => {
						const isActive = subcat.raw === selectedSubcategory;

						const localizedLabel = t(`type_subcategory.${subcat.raw}`);
						const labelLong = getTypeLabelLongFromSubcategory(subcat.raw);

						return (
							<OptionButtonLarge
								key={index}
								svgIcon={tShirtIconColor}
								onClick={() => {
									setSelectedSubcategory(subcat.raw);
									handleProductSelect({
										type_subcategory: subcat.raw,
										type_label_long: labelLong,
										brand_code: brand.code,
									});
								}}
								className={
									isActive
										? 'pop-border-1 pop-rounded-xl pop-border-popsizeSecondary90 pop-bg-popsizeSecondary20 pop-text-popsizeSecondary90'
										: 'pop-border-1 pop-rounded-xl pop-border-popsizePrimary50 pop-text-popsizePrimary90 hover:pop-border-popsizeSecondary90 hover:pop-bg-popsizeSecondary20'
								}
							>
								{localizedLabel}
							</OptionButtonLarge>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default TypeSelectionView;
